import React from "react";
import cn from "./helpers/cn";

// MUI v4 usa um sistema interno baseado em SIZE = 44 e raio = 20.2 (mantém
// a viewBox proporcional). Replicamos isso aqui — a prop `size` só altera
// o tamanho final via width/height; a geometria do SVG fica fixa.
const SIZE = 44;

export type CircularProgressColor = "primary" | "secondary" | "inherit";
export type CircularProgressVariant = "determinate" | "indeterminate" | "static";

interface CircularProgressProps extends Omit<React.SVGAttributes<SVGSVGElement>, "color"> {
  size?: number | string;
  thickness?: number;
  value?: number;
  variant?: CircularProgressVariant;
  color?: CircularProgressColor;
  disableShrink?: boolean;
}

const colorMap: Record<CircularProgressColor, string> = {
  primary: "text-primary",
  secondary: "text-secondary",
  inherit: "text-current",
};

const CircularProgress = React.forwardRef<SVGSVGElement, CircularProgressProps>((props, ref) => {
  const {
    size = 40,
    thickness = 3.6,
    value = 0,
    variant = "indeterminate",
    color = "primary",
    disableShrink = false,
    className,
    style,
    ...other
  } = props;

  const isDeterminate = variant === "determinate" || variant === "static";

  // Geometria do círculo no espaço da viewBox.
  const circumference = 2 * Math.PI * ((SIZE - thickness) / 2);

  const rootStyle: React.CSSProperties = {
    width: typeof size === "number" ? `${size}px` : size,
    height: typeof size === "number" ? `${size}px` : size,
    ...style,
  };

  // Em determinate, o stroke aparece girando para revelar o valor.
  // Em static, sem transição (estado fixo). Em indeterminate, ignorado.
  const circleStyle: React.CSSProperties = isDeterminate
    ? {
      strokeDasharray: circumference.toFixed(3),
      strokeDashoffset: `${(((100 - value) / 100) * circumference).toFixed(3)}px`,
      transform: "rotate(-90deg)",
      transformOrigin: "center",
      transition: variant === "determinate" ? "stroke-dashoffset 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0ms" : undefined,
    }
    : {};

  return (
    <svg
      ref={ref}
      viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`}
      style={rootStyle}
      role="progressbar"
      aria-valuenow={isDeterminate ? Math.round(value) : undefined}
      aria-valuemin={isDeterminate ? 0 : undefined}
      aria-valuemax={isDeterminate ? 100 : undefined}
      className={cn(
        "inline-block",
        colorMap[color],
        // SVG inteiro gira em indeterminate.
        variant === "indeterminate" && "animate-circular-rotate",
        className
      )}
      {...other}
    >
      <circle
        cx={SIZE}
        cy={SIZE}
        r={(SIZE - thickness) / 2}
        fill="none"
        stroke="currentColor"
        strokeWidth={thickness}
        // Em indeterminate, a animação anima dasharray/dashoffset. `disableShrink`
        // mantém o stroke estático e só roda o SVG (igual ao MUI v4).
        className={cn(
          variant === "indeterminate" && !disableShrink && "animate-circular-dash"
        )}
        style={circleStyle}
      />
    </svg>
  );
});

CircularProgress.displayName = "CircularProgress";

export default CircularProgress;
